'use client'

import dynamic from 'next/dynamic'
import { memo, ReactNode, Suspense, useEffect, useState } from 'react'
import classNames from 'classnames'
import { useTranslations } from '@dustin-web/microsite-components/src/hooks'
import { Skeleton } from '@dustin-web/microsite-components/src/skeleton'
import type { DigitalDataOrigin } from '@dustin-web/product-components/src/utils/digital-data-types'
import type { Product } from '@productsite-beta/src/graphql/generated/graphql-types'
import ProductCard from './product-card/product-card'

const ExpandButton = dynamic(() => import('@dustin-web/microsite-components/src/expand-button'))

import './product-highlights.scss'

type Props = {
  children?: ReactNode
  products: Product[]
  mainProductTicket: string
  titleKey: string
  digitalDataOrigin: DigitalDataOrigin
  isMobile: boolean
  disableCollapse?: boolean
  noSeparator?: boolean
}

const ProductHighlights = ({
  children,
  products: list,
  mainProductTicket,
  titleKey,
  digitalDataOrigin,
  isMobile,
  disableCollapse = false,
  noSeparator = false,
}: Props) => {
  const [showAll, setshowAll] = useState(disableCollapse)
  const { translate } = useTranslations()

  const initalProductCount = isMobile ? 1 : 3
  const products = showAll ? list : list.slice(0, initalProductCount)

  //This logic recenters the mobile view onto the list because the layout uses a grid.
  //If the list's position in the layout changes, this code must be re-evaluated.
  useEffect(() => {
    if (disableCollapse) {
      return
    }

    if (!isMobile || !showAll) {
      return
    }

    const frame = requestAnimationFrame(() => {
      document.getElementById('highlights-header')?.scrollIntoView({
        behavior: 'instant',
        block: 'start',
      })
    })

    return () => cancelAnimationFrame(frame)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAll])

  const toggleShowAll = () => {
    setshowAll(state => !state)
  }

  return (
    <div
      className={classNames('product-highlights', {
        'no-separator': noSeparator,
      })}>
      <div id="highlights-header" className="header">
        <h2 className="h4">{translate(titleKey)}</h2>
      </div>
      <div className="products-wrapper">
        <Suspense
          fallback={products.map((product, index) => {
            let skeletonHeight = isMobile ? 144 : 126

            if (product.energyClass) {
              skeletonHeight = isMobile ? 175 : 157
            }

            return <Skeleton key={index} height={skeletonHeight} />
          })}>
          {products?.map((product, key) => (
            <ProductCard
              key={key}
              digitalIndex={key}
              product={product as Product}
              isSmallScreen={isMobile}
              ticket={mainProductTicket}
              addToCartText={translate('Add')}
              digitalDataOrigin={digitalDataOrigin}
            />
          ))}
        </Suspense>
      </div>
      {!disableCollapse && list?.length > initalProductCount && (
        <ExpandButton
          className="u-pt-12"
          onClick={toggleShowAll}
          collapsedTitle={translate('ShowMore')}
          expandedTitle={translate('ShowLess')}
          expanded={showAll}
          ariaControls="expandable-highlights-cards"
        />
      )}
      {children}
    </div>
  )
}

export default memo(ProductHighlights)
